import React from 'react';
import { Eye, Heart, Clock, User, ExternalLink, Download, BookOpen, FileText, Video, Link, PenTool as Tool, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Resource } from '../../types/resources';
import { ResourceType, DifficultyLevel } from '../../types/resources';

interface ResourceCardProps {
  resource: Resource;
  onView: (resource: Resource) => void;
  onLike?: (resourceId: string) => void;
  showVisibilityBadge?: boolean;
  isLiked?: boolean;
}

export const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onView,
  onLike,
  showVisibilityBadge = false,
  isLiked = false
}) => {
  const getTypeIcon = (type: ResourceType) => {
    switch (type) {
      case ResourceType.ARTICLE:
        return <FileText className="w-5 h-5" />;
      case ResourceType.VIDEO:
        return <Video className="w-5 h-5" />;
      case ResourceType.DOCUMENT:
        return <BookOpen className="w-5 h-5" />;
      case ResourceType.LINK:
        return <ExternalLink className="w-5 h-5" />;
      case ResourceType.TOOL:
        return <Tool className="w-5 h-5" />;
      case ResourceType.CHECKLIST:
        return <CheckSquare className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type: ResourceType) => {
    const labels = {
      [ResourceType.ARTICLE]: 'Article',
      [ResourceType.VIDEO]: 'Vidéo',
      [ResourceType.DOCUMENT]: 'Document',
      [ResourceType.LINK]: 'Lien',
      [ResourceType.TOOL]: 'Outil',
      [ResourceType.TEMPLATE]: 'Template',
      [ResourceType.GUIDE]: 'Guide',
      [ResourceType.CHECKLIST]: 'Checklist'
    };
    return labels[type] || type;
  };

  const getDifficultyColor = (level: DifficultyLevel) => {
    switch (level) {
      case DifficultyLevel.BEGINNER:
        return 'bg-green-100 text-green-800';
      case DifficultyLevel.INTERMEDIATE:
        return 'bg-yellow-100 text-yellow-800';
      case DifficultyLevel.ADVANCED:
        return 'bg-orange-100 text-orange-800';
      case DifficultyLevel.EXPERT:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyLabel = (level: DifficultyLevel) => {
    const labels = {
      [DifficultyLevel.BEGINNER]: 'Débutant',
      [DifficultyLevel.INTERMEDIATE]: 'Intermédiaire',
      [DifficultyLevel.ADVANCED]: 'Avancé',
      [DifficultyLevel.EXPERT]: 'Expert'
    };
    return labels[level] || level;
  };

  const getCategoryLabel = (category: string) => {
    const labels = {
      'BUSINESS_MODEL': 'Modèle Économique',
      'LEGAL_STRUCTURE': 'Statut Juridique',
      'FINANCING': 'Financement',
      'MARKETING': 'Marketing',
      'MANAGEMENT': 'Gestion',
      'IMPACT_MEASUREMENT': 'Mesure d\'Impact',
      'NETWORKING': 'Réseautage',
      'DIGITAL_TOOLS': 'Outils Numériques',
      'SUSTAINABILITY': 'Développement Durable',
      'INNOVATION': 'Innovation Sociale'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const handleCardClick = () => {
    onView(resource);
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLike) {
      onLike(resource.id);
    }
  };

  const handleExternalLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resource.external_url) {
      window.open(resource.external_url, '_blank');
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer group animate-slide-up"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
            {getTypeIcon(resource.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {getTypeLabel(resource.type)}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(resource.difficulty_level)}`}>
                {getDifficultyLabel(resource.difficulty_level)}
              </span>
              {resource.is_featured && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  ⭐ À la une
                </span>
              )}
              {showVisibilityBadge && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  👁️ Visible
                </span>
              )}
            </div>
          </div>
        </div>
        
        {resource.external_url && (
          <button
            onClick={handleExternalLink}
            className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"
            title="Lien externe"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
          {resource.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3 mb-3">
          {resource.description}
        </p>
        
        <div className="flex items-center space-x-2 mb-3">
          <span className="text-xs text-blue-600 font-medium">
            {getCategoryLabel(resource.category)}
          </span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {resource.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
              {tag}
            </span>
          ))}
          {resource.tags.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
              +{resource.tags.length - 3}
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Eye className="w-4 h-4" />
            <span>{resource.view_count}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{resource.estimated_reading_time} min</span>
          </div>
          {resource.author_name && (
            <div className="flex items-center space-x-1">
              <User className="w-4 h-4" />
              <span>{resource.author_name}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {onLike && (
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1 px-2 py-1 rounded-md transition-colors ${
                isLiked 
                  ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                  : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
              }`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
              <span>{resource.like_count}</span>
            </button>
          )}
          <span className="text-xs">
            {format(new Date(resource.created_at), 'dd MMM', { locale: fr })}
          </span>
        </div>
      </div>
    </div>
  );
};